import { isDefined } from '@sgme/fp';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useAlgoTileId, useValidateAlgo } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectAlgoErrors,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromAlgoTile,
} from '@/store/orders/algoOrders/selectors.ts';

type AlgoSpreadCaptureProps = {
  spreadCapture: boolean | undefined;
  setSpreadCapture: (value: boolean | undefined) => void;
};

export const AlgoEditingSpreadCapture = ({ spreadCapture, setSpreadCapture }: AlgoSpreadCaptureProps) => {
  const tileId = useAlgoTileId();
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const value =
    spreadCapture ?? (isDefined(orderFromBlotter?.spreadCapture) ? orderFromBlotter?.spreadCapture : undefined);
  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const validateAlgo = useValidateAlgo();

  const handleSpreadCaptureChange = async (newValue: boolean) => {
    track('Order action', 'Spread capture changed');
    const response = await validateAlgo({
      spreadCapture: newValue,
    });

    if (response?.data?.validationResponseDetailContent.length === 0) {
      setSpreadCapture(newValue);
    }
  };

  const spreadCaptureError = errors?.spreadCapture;

  return (
    <OrderRow labelId="order.fields.spreadCapture">
      <OmsFieldErrorWrapper errors={spreadCaptureError}>
        <div className="form-check form-check-sm">
          <input
            id="spreadCapture"
            className="form-check-input me-8px"
            type="checkbox"
            checked={value}
            onChange={(e) => handleSpreadCaptureChange(e.target.checked)}
            data-e2e={'spread-capture-checkbox'}
          />
        </div>
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
